/* eslint-disable react-hooks/exhaustive-deps */
import { useAdminUser, useAlert, useCompany } from '@orientaction/hooks';
import { adminService } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { generate } from 'generate-password-browser';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getUserById } from '../../../services/user.service';
import { apiServiceNative } from '../../../utils/fetchNative';
import { inCardService, oaServices } from '../../../components/Form/CreateEditAdmin/constant';
import { BENEFICIARY_ROLE_ID, CONSULTANT_ROLE_ID } from '../shared/constants';
import { ServiceOption } from '../shared/types';
import { readRole } from '../shared/utils';
import { readAllowedServiceIds } from './utils';
import {
  ADD_PRESTATION_SUCCESS,
  ConsultantProfileTabId,
  DELETE_PRESTATION_SUCCESS,
  DELETE_USER_SUCCESS,
  DISABLE_USER_SUCCESS,
  ENABLE_USER_SUCCESS,
  getProfileTabs,
} from './constants';

const qs = require('qs');

type ConfirmType = 'disable' | 'enable' | 'delete' | '';

interface PrestationConfirmState {
  open: boolean;
  service: { id: number; name: string } | null;
}

const emptyPrestationConfirm: PrestationConfirmState = {
  open: false,
  service: null,
};

const ALL_TAB_IDS = new Set<string>([
  'profil',
  'prestations',
  'tests-mineurs',
  'beneficiaires',
  'manager_3',
  'manager_2',
  'manager_1',
]);

const readInitialTab = (search: string): ConsultantProfileTabId => {
  const params = new URLSearchParams(search);
  const requested = params.get('tab');
  if (requested && ALL_TAB_IDS.has(requested)) {
    return requested as ConsultantProfileTabId;
  }
  return 'profil';
};

export const useConsultantProfile = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const alert = useAlert();
  const { setTabs } = useAdminUser();
  const { getPopulateHierachy } = useCompany();
  const token = getFromLS('userToken') || '';

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<ConsultantProfileTabId>(() => readInitialTab(location.search));
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; type: ConfirmType }>({
    open: false,
    type: '',
  });
  const [confirming, setConfirming] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);
  const [passwordModal, setPasswordModal] = useState({ open: false, password: '' });
  const [prestationConfirm, setPrestationConfirm] =
    useState<PrestationConfirmState>(emptyPrestationConfirm);
  const [deletingPrestation, setDeletingPrestation] = useState(false);
  const [addPrestationOpen, setAddPrestationOpen] = useState(false);
  const [addingPrestation, setAddingPrestation] = useState(false);
  const [prestationsReloadKey, setPrestationsReloadKey] = useState(0);
  const [hierarchy, setHierarchy] = useState<Record<string, any[]>>({});
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [hierarchyReloadKey, setHierarchyReloadKey] = useState(0);

  const roleId = readRole(user?.company_user_role)?.id;

  const fetchProfileContext = async () => {
    const params = qs.stringify(
      {
        populate: {
          company_user_role: true,
          upper_hierarchy: { fields: ['id', 'firstname', 'lastname'] },
          category_tests: { fields: ['id', 'name', 'subtitle', 'priority'] },
          allowed_services: { fields: ['id', 'name'] },
        },
      },
      { encodeValuesOnly: true }
    );
    try {
      return await apiServiceNative.get(`/api/users/${id}?${params}`);
    } catch {
      return null;
    }
  };

  const fetchUser = async (saved?: any) => {
    const [response, contextPayload] = await Promise.all([
      getUserById({ token, userId: id }),
      fetchProfileContext(),
    ]);
    const next = response?.data || response;
    const context = contextPayload?.data || contextPayload || {};
    const merged = {
      ...next,
      ...saved,
      company_user_role: context.company_user_role ?? saved?.company_user_role ?? next?.company_user_role,
      upper_hierarchy: context.upper_hierarchy ?? saved?.upper_hierarchy ?? next?.upper_hierarchy,
      category_tests: context.category_tests ?? saved?.category_tests ?? next?.category_tests,
      allowed_services:
        saved?.allowed_services ?? context.allowed_services ?? next?.allowed_services,
    };
    const mergedRoleId = readRole(merged.company_user_role)?.id;
    if (mergedRoleId === BENEFICIARY_ROLE_ID) {
      history.replace(`/admin/users-resultats/beneficiaire/${id}`);
      return;
    }
    setUser(merged);
  };

  const loadUser = async () => {
    setLoading(true);
    try {
      await fetchUser();
    } catch {
      alert.openSimpleAlert();
      history.push('/admin/users-resultats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [id]);

  useEffect(() => {
    if (!roleId) {
      return;
    }
    const available = getProfileTabs(roleId).map(item => item.id);
    if (!available.includes(tab)) {
      setTab('profil');
    }
  }, [roleId, tab]);

  useEffect(() => {
    if (!user?.id || roleId === CONSULTANT_ROLE_ID || !roleId) {
      setHierarchy({});
      return undefined;
    }

    let cancelled = false;
    const loadHierarchy = async () => {
      setLoadingHierarchy(true);
      try {
        const response = await getPopulateHierachy(user.id);
        if (!cancelled) {
          setHierarchy(response || {});
        }
      } catch {
        if (!cancelled) {
          setHierarchy({});
        }
      } finally {
        if (!cancelled) {
          setLoadingHierarchy(false);
        }
      }
    };

    loadHierarchy();
    return () => {
      cancelled = true;
    };
  }, [user?.id, roleId, hierarchyReloadKey]);

  const goBack = () => {
    if (roleId) {
      setTabs(roleId);
    }
    history.push('/admin/users-resultats');
  };

  const closeConfirmModal = () => {
    if (confirming) {
      return;
    }
    setConfirmModal(prev => ({ ...prev, open: false }));
  };

  const closePasswordModal = () => {
    setPasswordModal({ open: false, password: '' });
  };

  const generatePassword = async () => {
    if (!user || generatingPassword) {
      return;
    }
    setGeneratingPassword(true);
    try {
      const generatedPassword = generate({
        length: 10,
        lowercase: true,
        uppercase: true,
        numbers: true,
        symbols: true,
        exclude: '$',
        strict: true,
      });
      await adminService.generateUserPassword(user.id, generatedPassword, token);
      setPasswordModal({ open: true, password: generatedPassword });
    } catch {
      alert.openSimpleAlert();
    } finally {
      setGeneratingPassword(false);
    }
  };

  const askDisable = () => {
    if (!user) {
      return;
    }
    setConfirmModal({
      open: true,
      type: user.isActivated ? 'disable' : 'enable',
    });
  };

  const askDelete = () => {
    setConfirmModal({
      open: true,
      type: 'delete',
    });
  };

  const confirmAction = async () => {
    if (!user || !confirmModal.type) {
      return;
    }
    setConfirming(true);
    try {
      if (confirmModal.type === 'disable' || confirmModal.type === 'enable') {
        await adminService.disableAdmin(user.id, !user.isActivated, token);
        await fetchUser();
        setConfirmModal(prev => ({ ...prev, open: false }));
        alert.setOpenAlert(
          true,
          confirmModal.type === 'disable' ? DISABLE_USER_SUCCESS : ENABLE_USER_SUCCESS,
          'success'
        );
        return;
      }
      if (confirmModal.type === 'delete') {
        await adminService.deleteAdmin(
          { id: user.id, companyUserRole: roleId || 4 },
          token
        );
        setConfirmModal(prev => ({ ...prev, open: false }));
        if (roleId) {
          setTabs(roleId);
        }
        alert.setOpenAlert(true, DELETE_USER_SUCCESS, 'success');
        history.push('/admin/users-resultats');
      }
    } catch {
      alert.openSimpleAlert();
    } finally {
      setConfirming(false);
    }
  };

  const askDeletePrestation = (service: { id: number; name?: string }) => {
    if (!service?.id) {
      return;
    }
    setPrestationConfirm({
      open: true,
      service: { id: Number(service.id), name: service.name || `Service ${service.id}` },
    });
  };

  const closePrestationConfirm = () => {
    if (deletingPrestation) {
      return;
    }
    setPrestationConfirm(emptyPrestationConfirm);
  };

  const confirmDeletePrestation = async () => {
    const serviceId = Number(prestationConfirm.service?.id);
    if (!user?.id || !serviceId || deletingPrestation) {
      return;
    }
    setDeletingPrestation(true);
    try {
      const remaining = readAllowedServiceIds(user)
        .filter(allowedId => allowedId !== serviceId)
        .map(allowedId => ({ id: allowedId }));
      await apiServiceNative.put(`/api/users/${user.id}`, {
        allowed_services: remaining,
      });
      setUser((current: any) =>
        current
          ? {
              ...current,
              allowed_services: remaining,
            }
          : current
      );
      setPrestationConfirm(emptyPrestationConfirm);
      setPrestationsReloadKey(current => current + 1);
      alert.setOpenAlert(true, DELETE_PRESTATION_SUCCESS, 'success');
      try {
        await fetchUser();
      } catch {
        // The local allowed_services list is already updated.
      }
    } catch {
      alert.openSimpleAlert();
    } finally {
      setDeletingPrestation(false);
    }
  };

  const goToAddPrestation = () => {
    setAddPrestationOpen(true);
  };

  const closeAddPrestation = () => {
    if (addingPrestation) {
      return;
    }
    setAddPrestationOpen(false);
  };

  const availablePrestations = useMemo(() => {
    const attached = new Set(readAllowedServiceIds(user));
    return oaServices
      .concat(inCardService)
      .map(
        (service: any): ServiceOption => ({
          id: Number(service.id),
          name: service.attributes?.name || `Service ${service.id}`,
        })
      )
      .filter(service => service.id && !attached.has(service.id));
  }, [user?.allowed_services]);

  const confirmAddPrestation = async (serviceId: number) => {
    if (!user?.id || !serviceId || addingPrestation) {
      return;
    }
    const alreadyAttached = readAllowedServiceIds(user);
    if (alreadyAttached.includes(serviceId)) {
      setAddPrestationOpen(false);
      return;
    }
    setAddingPrestation(true);
    try {
      const nextServices = [...alreadyAttached, serviceId].map(allowedId => ({ id: allowedId }));
      await apiServiceNative.put(`/api/users/${user.id}`, {
        allowed_services: nextServices,
      });
      setUser((current: any) =>
        current
          ? {
              ...current,
              allowed_services: nextServices,
            }
          : current
      );
      setAddPrestationOpen(false);
      setPrestationsReloadKey(current => current + 1);
      alert.setOpenAlert(true, ADD_PRESTATION_SUCCESS, 'success');
      try {
        await fetchUser({ allowed_services: nextServices });
      } catch {
        // The local allowed_services list is already updated.
      }
    } catch {
      alert.openSimpleAlert();
    } finally {
      setAddingPrestation(false);
    }
  };

  return {
    user,
    loading,
    tab,
    setTab,
    editingProfile,
    setEditingProfile,
    roleId,
    hierarchy,
    loadingHierarchy,
    reloadHierarchy: () => setHierarchyReloadKey(current => current + 1),
    fetchUser,
    confirmModal,
    confirming,
    generatingPassword,
    passwordModal,
    goBack,
    generatePassword,
    closePasswordModal,
    askDisable,
    askDelete,
    closeConfirmModal,
    confirmAction,
    prestationConfirm,
    deletingPrestation,
    prestationsReloadKey,
    askDeletePrestation,
    closePrestationConfirm,
    confirmDeletePrestation,
    addPrestationOpen,
    addingPrestation,
    availablePrestations,
    goToAddPrestation,
    closeAddPrestation,
    confirmAddPrestation,
  };
};
